import { IEventsState } from './types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  getSchedulesThunk,
  createPostThunk,
  createLabelThunk,
  updateLabelThunk,
  deleteLabelThunk,
  softDeletePostThunk,
  batchSoftDeletePostsThunk
} from './thunks'

import {
  updateScheduleThunk,
  updateSchedulesThunk,
  deleteScheduleThunk
} from '../managePost/thunks'
import { postApprovalPostsThunk } from '../modal/approvalPost/thunks'
import { duplicatePostThunk } from '../modal/duplicatePost/thunks'
import { POST_SOCIAL_STATUS, POST_STATUS } from '../../models/post'

const initialState: IEventsState = {
  schedules: [],
  selectedSchedules: [],
  loaders: {
    schedules: {
      loading: false,
      requestQty: 0
    }
  },
  currentProjectId: null,
  isCreatingPost: false,
  isCreatingLabel: false,
  isDeletingLabel: false,
  error: null,
  dayLabels: [],
  requestRevisionFormCalendarApproval:false
}

const eventsApprovalSlice = createSlice({
  name: 'calendarApproval',
  initialState,
  reducers: {
    setPostDateAction: (state, action: PayloadAction<any>) => {
      const { id, date } = action.payload
      state.schedules = state.schedules.map((post: any) =>
        post.id === id ? { ...post, date } : post
      )
    },
    setSelectedSchedulesAction: (state, action: PayloadAction<any>) => {
      state.selectedSchedules = action.payload
    },
    setRequestRevisionFormCalendarApprovalAction: (state, action: PayloadAction<any>) => {
      state.requestRevisionFormCalendarApproval = action.payload
    }
  },
  extraReducers: (builder) => {
    // SCHEDULES

    // get schedules
    builder.addCase(getSchedulesThunk.pending, (state, action) => {
      state.currentProjectId = action.meta.arg.id
      state.loaders.schedules.requestQty = state.loaders.schedules.requestQty + 1
      if (!state.loaders.schedules.loading) {
        state.loaders.schedules.loading = true
        state.error = null
      }
    })
    builder.addCase(getSchedulesThunk.fulfilled, (state, action) => {
      const projectIdCurrent = state.currentProjectId
      const projectIdRequest = action.meta.arg.id
      state.startDate = action.meta.arg.start;
      state.endDate = action.meta.arg.end;
      state.loaders.schedules.requestQty = state.loaders.schedules.requestQty - 1
      if (projectIdCurrent === projectIdRequest) {

        state.loaders.schedules.loading = false
        state.schedules = action.payload.data.post_schedules
        state.dayLabels = action.payload.data.day_labels
      }
    })
    builder.addCase(getSchedulesThunk.rejected, (state, action) => {
      state.loaders.schedules.requestQty = state.loaders.schedules.requestQty - 1
      if (!state.loaders.schedules.requestQty) {
        state.loaders.schedules.loading = false
        state.error = action.payload
      }
    })

    // POSTS

    // create post
    builder.addCase(createPostThunk.pending, (state, action) => {
      state.isCreatingPost = true
      state.error = null
    })
    builder.addCase(createPostThunk.fulfilled, (state, action) => {
      state.isCreatingPost = false
      state.schedules.push(action.payload)
    })
    builder.addCase(createPostThunk.rejected, (state, action) => {
      state.isCreatingPost = false
      state.error = action.payload
    })
    // Delete post
    builder.addCase(softDeletePostThunk.pending, (state, action) => { })
    builder.addCase(softDeletePostThunk.fulfilled, (state, action) => {
      const deletedId = action.meta.arg
      state.schedules = state.schedules.filter((schedule) => deletedId !== schedule.post.id)
    })
    builder.addCase(softDeletePostThunk.rejected, (state, action) => { })
    // butch delete posts
    builder.addCase(batchSoftDeletePostsThunk.pending, (state, action) => { })
    builder.addCase(batchSoftDeletePostsThunk.fulfilled, (state, action) => {
      const deletedIds = action.meta.arg
      state.schedules = state.schedules.filter((schedule) => !deletedIds.includes(schedule.post.id))
    })
    builder.addCase(batchSoftDeletePostsThunk.rejected, (state, action) => { })

    // LABELS

    // create new label
    builder.addCase(createLabelThunk.pending, (state, action) => {
      state.isCreatingLabel = true
      state.error = null
    })
    builder.addCase(createLabelThunk.fulfilled, (state, action) => {
      state.isCreatingLabel = false
      state.dayLabels.push(action.payload.data)
    })
    builder.addCase(createLabelThunk.rejected, (state, action) => {
      state.isCreatingLabel = false
      state.error = action.payload
    })

    // update label
    builder.addCase(updateLabelThunk.pending, (state, action) => {
      state.isCreatingLabel = true
      state.error = null
    })
    builder.addCase(updateLabelThunk.fulfilled, (state, action) => {
      const updatedLabel = action.payload.data
      state.isCreatingLabel = false
      state.dayLabels = state.dayLabels.map((label) =>
        label.id === updatedLabel.id ? updatedLabel : label
      )
    })
    builder.addCase(updateLabelThunk.rejected, (state, action) => {
      state.isCreatingLabel = false
      state.error = action.payload
    })
    // Update butch of schedules
    builder.addCase(updateSchedulesThunk.fulfilled, (state, action) => {
      const updatedSchedules = action.payload.data
      state.schedules = state.schedules.map((schedule) => {
        for (let updatedSchedule of updatedSchedules) {
          if (updatedSchedule.id === schedule.id) return updatedSchedule
        }
        return schedule
      })
    })
    builder.addCase(deleteScheduleThunk.fulfilled, (state, action) => {
      state.schedules = state.schedules.filter((post) => post.id !== action.meta.arg)
    })
    // Update single schedule
    builder.addCase(updateScheduleThunk.fulfilled, (state, action) => {
      const updatedSchedule = action.payload
      state.schedules = state.schedules.map((schedule) =>
        schedule.id !== updatedSchedule.id ? schedule : updatedSchedule
      )
    })
    // delete label
    builder.addCase(deleteLabelThunk.pending, (state, action) => {
      state.isDeletingLabel = true
      state.error = null
    })
    builder.addCase(deleteLabelThunk.fulfilled, (state, action) => {
      state.isDeletingLabel = false
      state.dayLabels = state.dayLabels.filter((label) => label.id !== Number(action.payload))
    })
    builder.addCase(deleteLabelThunk.rejected, (state, action) => {
      state.isDeletingLabel = false
      state.error = action.payload
    })

    // update social status for posts after approve
    builder.addCase(postApprovalPostsThunk.fulfilled, (state, action) => {
      const updatedPosts = action.payload.data
      state.schedules = state.schedules.map((schedule) => {
        for (let post of updatedPosts) {
          if (post.post_id === schedule.post.id) {
            return {
              ...schedule,
              post: {
                ...schedule.post,
                status: POST_STATUS.STATUS_PENDING_APPROVAL,
                post_socials: schedule.post.post_socials.map((postSocial: any) => ({
                  ...postSocial,
                  status: POST_SOCIAL_STATUS.STATUS_PENDING_INTERNAL_APPROVAL
                }))
              }
            }
          }
        }
        return schedule
      })
    })

    // Duplicate post
    builder.addCase(duplicatePostThunk.fulfilled, (state, action) => {
      const { latest_schedule, ...post } = action.payload
      state.schedules.push({ ...latest_schedule, post })
    })
  }
})

export const { setPostDateAction, setSelectedSchedulesAction,setRequestRevisionFormCalendarApprovalAction } = eventsApprovalSlice.actions
export default eventsApprovalSlice.reducer
